import { Button, FlexBox, Tag } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useHasAccess } from 'lib-shared-access-control';
import type {
  EntryEventTrigger,
  MimTaskTrigger,
  RoutedThroughAutomationEntry,
} from 'lib-shared-legacy-types';
import { useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { LogEntry } from './logEntry';
import { AutomationActivityType } from './routedThroughAutomationNodes/types/automationActivity';

type Props = {
  entry: RoutedThroughAutomationEntry;
};

function isEntryEventTrigger(
  eventTrigger: EntryEventTrigger | null,
): eventTrigger is EntryEventTrigger {
  return eventTrigger !== null;
}

function isMimTaskEventTrigger(
  eventTrigger: EntryEventTrigger | null,
): eventTrigger is MimTaskTrigger {
  return isEntryEventTrigger(eventTrigger) && eventTrigger.type === 'mim_task';
}

export const RoutedThroughTask = ({ entry }: Props): JSX.Element | null => {
  const { csid, execution_id: executionId, rule_name: automationName } = entry;

  const navigate = useAppNavigate();

  const {
    data: {
      data: { automation_activities: automationActivities = [] } = {},
    } = {},
  } = conversationsApi.useReadConversationAutomationActivitiesQuery(
    {
      csid,
      automationRunId: executionId ?? '',
    },
    {
      skip: !executionId,
    },
  );

  const hasAutomationReadAccess = useHasAccess('settings.flow:r');

  const handleGoToMimTask = useCallback(() => {
    const chatbotId = isMimTaskEventTrigger(entry.event_trigger)
      ? entry.event_trigger.chatbot_id
      : null;

    if (chatbotId) {
      navigate({
        path: '/settings/chatbots/mim/:id/tasks/:taskId',
        params: {
          path: {
            id: chatbotId,
            taskId: entry.rule_id,
          },
        },
      });
    } else {
      navigate({
        path: '/settings/chatbots/mim',
      });
    }
  }, [entry.event_trigger, entry.rule_id, navigate]);

  const integrationNode = automationActivities.find(
    (node) =>
      node.activity_type ===
      AutomationActivityType.AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED,
  );
  const outcome = integrationNode?.outcome;
  const outcomeWasSuccess = outcome?.type === 'success';

  const outcomeText = (() => {
    if (!outcome) {
      return 'Status unknown';
    }

    if (outcomeWasSuccess) {
      return 'Mim task ran successfully';
    }

    if (outcome?.type === 'timeout') {
      return 'Failed to run Mim task (timeout)';
    }

    return 'Failed to run Mim task';
  })();

  const outcomeColor = (() => {
    if (!outcome) {
      return colors.neutral[400];
    }

    if (outcomeWasSuccess) {
      return colors.green[400];
    }

    return colors.red[400];
  })();

  if (!executionId) {
    return null;
  }

  return (
    <LogEntry
      entry={entry}
      icon="route"
      text={`Ran Mim task${automationName ? `: ${automationName}` : ''}`}
      isExpandable
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {outcome && (
          <Tag color={outcomeColor} noBorder>
            {outcomeText}
          </Tag>
        )}

        {hasAutomationReadAccess && (
          <FlexBox margin="300 0">
            <Button variant="secondary" onClick={handleGoToMimTask}>
              Go to Mim task
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
